import BaseObjectDirective from './BaseObjectDirective';
import Countable from 'countable';

// Available tools configuration
const availableTools = [
    { id: 'tool_alignment', name: 'alignment' },
    { id: 'tool_blockQuote', name: 'blockQuote' },
    { id: 'tool_bold', name: 'bold', default: true },
    { id: 'tool_bulletedList', name: 'bulletedList', default: true },
    { id: 'tool_caseChange', name: 'caseChange' },
    { id: 'tool_calculator', name: 'calculator', default: true },
    { id: 'tool_ChemType', name: 'ChemType' },
    { id: 'tool_code', name: 'code' },
    { id: 'tool_codeBlock', name: 'codeBlock' },
    { id: 'tool_findAndReplace', name: 'findAndReplace' },
    { id: 'tool_fontBackgroundColor', name: 'fontBackgroundColor' },
    { id: 'tool_fontColor', name: 'fontColor' },
    { id: 'tool_fontFamily', name: 'fontFamily' },
    { id: 'tool_fontSize', name: 'fontSize' },
    { id: 'tool_heading', name: 'heading' },
    { id: 'tool_highlight', name: 'highlight' },
    { id: 'tool_horizontalLine', name: 'horizontalLine' },
    { id: 'tool_imagePalette', name: 'imagePalette' },
    { id: 'tool_indent', name: 'indent' },
    { id: 'tool_insertTable', name: 'insertTable' },
    { id: 'tool_italic', name: 'italic', default: true },
    { id: 'tool_mediaEmbed', name: 'mediaEmbed' },
    { id: 'tool_numberedList', name: 'numberedList', default: true },
    { id: 'tool_outdent', name: 'outdent' },
    { id: 'tool_redo', name: 'redo', default: true },
    { id: 'tool_revisionHistory', name: 'revisionHistory' },
    { id: 'tool_restrictedEditingException', name: 'restrictedEditingException' },
    { id: 'tool_sourceEditing', name: 'sourceEditing' },
    { id: 'tool_specialCharacters', name: 'specialCharacters', default: true },
    { id: 'tool_style', name: 'style', default: true },
    { id: 'tool_selectAll', name: 'selectAll' },
    { id: 'tool_subscript', name: 'subscript', default: true },
    { id: 'tool_superscript', name: 'superscript', default: true },
    { id: 'tool_tableOfContents', name: 'tableOfContents' },
    { id: 'tool_templates', name: 'templates' },
    { id: 'tool_tooltip', name: 'tooltip' },
    { id: 'tool_todoList', name: 'todoList' },
    { id: 'tool_underline', name: 'underline', default: true },
    { id: 'tool_undo', name: 'undo', default: true },
    { id: 'tool_wiris', name: 'wiris', default: true },
];

const heightOptions = ['extra small', 'small', 'medium', 'large'];
const heightOptionToPixels = {
    'extra small': 75,
    'small': 150,
    'medium': 300,
    'large': 450
};

/**
 * AsfResponseTextboxObjectNewCtrl is a controller class for handling the behavior of a text box object in a response.
 * It extends the BaseObjectDirective and provides functionality for CKEditor 5.
 */
export class AsfResponseTextboxObjectNewCtrl extends BaseObjectDirective {
    constructor($injector, $scope, $rootScope, $signals, $timeout, SheetLoadedService, $q, $bridges, $translate) {
        super($injector, $scope);

        this.$scope = $scope;
        this.$rootScope = $rootScope;
        this.$timeout = $timeout;
        this.$q = $q;
        this.$bridges = $bridges;
        this.SheetLoadedService = SheetLoadedService;

        const TaskInfoService = $injector.get('TaskInfoService');
        const taskInfo = TaskInfoService.getTaskInfo();
        const assessmentLanguage = taskInfo.getLanguageCode();

        this.$scope.isCMS = $rootScope.isCMS && !$rootScope.isPreview;
        this.$scope.isPreview = $rootScope.isPreview;
        this.$scope.editMode = false;
        this.$scope.prefsMode = false;
        this.$scope.wordcount = 0;

        // Set language and direction
        this.$scope.langISO = taskInfo.getISOInputLanguageCode();
        this.$scope.writingDirection = $rootScope.isJournal ? "ltr" : taskInfo.getInputLanguage().writingDirection;

        // Initialize content model
        this.$scope.rteModel = this.$scope.contentObject.getContent();
        
        this.initPreferences(taskInfo, assessmentLanguage);

        // Read some basic stuff
        const frontendMode = !this.$scope.isCMS || this.$rootScope.isPreview;
        this.$scope.objectUUID = this.$scope.contentObject.getUUID();
        window.contentObject = this.$scope.contentObject;
        angular.element(document).off('keydown');
        
        if (!frontendMode && !this.$scope.richtext) {
            this.$scope.prefsMode = false;
            this.$scope.hasEditMode = false;
        }

        if (frontendMode) {
            angular.element(document).off('keydown');
            return;
        }


        // Initialize editor handlers
        this.initializeEditorHandlers();

        // Initialize SheetLoadedService
        const _d = this.$q.defer();
        this.SheetLoadedService.wait(_d.promise, 'response-textbox-ck5');
        this.$timeout(() => {
            _d.resolve('response-textbox-ck5');
        });
    }

    initializeEditorHandlers() {
        // Add content change handler
        this.$scope.onEditorCallback = (e) => {
            if (this.$scope.editMode) {
                this.$scope.contentObject.setContent(e.value);
                this.$scope.rteModel = e.value;
            }
        };

        // Toggle edit mode
        this.$scope.toggleEditMode = () => {
            const elementWithId = angular.element(document.getElementById(this.$scope.contentObject.getUUID() + '_ck5'));
            const ckEditorEditableInline = elementWithId[0].nextSibling;
            const editorInstance = ckEditorEditableInline.querySelector('.ck-editor__editable').ckeditorInstance;
            editorInstance.isReadOnly ? editorInstance.disableReadOnlyMode('lock') : editorInstance.enableReadOnlyMode('lock');
        };

        // Reset changes
        this.$scope.resetChanges = () => {
            const elementWithId = angular.element(document.getElementById(this.$scope.contentObject.getUUID() + '_ck5'));
            const ckEditorEditableInline = elementWithId[0].nextSibling;
            const editorInstance = ckEditorEditableInline.querySelector('.ck-editor__editable').ckeditorInstance;
            editorInstance.setData('');
            this.$scope.contentObject.setContent('');
            this.$scope.rteModel = '';
        };

        this.$scope.onFocus = () => {
            this.$bridges.runtime().setLastKeyboardLanguage();
        };
    }

    initPreferences(taskInfo, assessmentLanguage) {
        const getPreferencesOrDefault = (preferencesKey, defaultValue) => {
            const preferences = this.$scope.contentObject.getPreferences();
            if (typeof preferences !== "undefined" && preferences.hasOwnProperty(preferencesKey)) {
                return preferences[preferencesKey];
            }
            return defaultValue;
        };

        // Get all preferences
        const height = getPreferencesOrDefault('height', null);
        const autogrow = getPreferencesOrDefault('autogrow', null);
        const objName = getPreferencesOrDefault('objName', undefined);
        const includedInPDF = getPreferencesOrDefault('includedInPDF', true);
        const restrictedEditingMode = getPreferencesOrDefault('restrictedEditingMode', false);
        const showToolbar = getPreferencesOrDefault('showToolbar', true);
        const placeholder = getPreferencesOrDefault('placeholder', null);
        const richtext = getPreferencesOrDefault('richtext', true);
        const enabledTools = getPreferencesOrDefault('enabledTools', true);
        const disabledTools = getPreferencesOrDefault('disabledTools', false);
        const wordcount = getPreferencesOrDefault('wordcount', false);

        this.$scope.richtext = richtext;

        // Handle spellchecker
        const languages = ['English', 'Français', 'Español', 'Deutsch', 'Nederlands'];
        const lang_codes = ['en', 'fr', 'es', 'de', 'nl'];
        let spellchecker_language = getPreferencesOrDefault('spellchecker_language', 
            languages[lang_codes.indexOf(assessmentLanguage)]);

        if (taskInfo.isInputLanguageCode()) {
            spellchecker_language = languages[lang_codes.indexOf(taskInfo.getSpellcheckerLanguageCode())];
            this.$scope.langISO = this.$rootScope.isJournal ? this.$translate.preferredLanguage() : 
                taskInfo.getISOInputLanguageCode();
        }

        // Set preferences values
        this.$scope.preferencesValues = {
            height,
            placeholder,
            autogrow,
            wordcount,
            richtext,
            objName,
            includedInPDF,
            restrictedEditingMode,
            showToolbar,
            enabledTools,
            disabledTools,
            spellchecker_language,
            spellchecker_is_enabled: getPreferencesOrDefault('spellchecker_is_enabled', false)
        };

        // Watch for changes
        this.$scope.$watch('preferencesValues', (val1, val2) => {
            if (val1 === val2) return;
            
            const preferences = {...val1};
            if (preferences.enabledTools.length === 0) {
                preferences.disabledTools = true;
                this.$scope.preferencesValues.disabledTools = true;
            }
            
            this.$scope.contentObject.setPreferences(preferences);
        }, true);

        // Set preferences UI configuration
        this.$scope.preferences = [
            {
                'placeholder': [
                    {'type': 'textarea', 'name': 'Placeholder text', 'valueKey': 'placeholder'}
                ],
                'includedInPDF': [
                    {'type': 'checkbox', 'name': 'Included in Response PDF', 'valueKey': 'includedInPDF'}
                ],
                'objName': [
                    {'type': 'input', 'name': 'Object Name', 'valueKey': 'objName'}
                ],
                'height': [
                    {
                        'type': 'select',
                        'name': 'Initial Height',
                        'valueKey': 'height',
                        options: heightOptions,
                        'noDummyOption': true
                    }
                ]
            },
            {
                'title': [
                    {'type': 'title', 'name': 'Candidate View Settings', 'valueKey': 'CandidateViewSettings'}
                ],
                'enabledTools': [
                    {
                        'type': 'multiselect-small',
                        'name': 'Choose Tools:',
                        'tools': availableTools,
                        'valueKey': 'enabledTools'
                    }
                ],
                'restrictedEditingMode': [
                    {'type': 'checkbox', 'name': 'Restricted Editing Mode?', 'valueKey': 'restrictedEditingMode'}
                ],
                'showToolbar': [
                    {'type': 'checkbox', 'name': 'Show Toolbar?', 'valueKey': 'showToolbar'}
                ],
                'wordcount': [
                    {'type': 'checkbox', 'name': 'Word count?', 'valueKey': 'wordcount'}
                ]
            }
        ];
    }
}

export function registerAsfResponseTextboxObjectNewDirective(module) {
    // Register the content object
    module.run(['ContentObjectRegistryService', function(ContentObjectRegistryService) {
        const obj = [{
            "uuid": "",
            "content": "",
            "objectType": "response-textbox-new",
            "type": "contentObject",
            "entity_content": "",
            "preferences": {
                "wordcount": true,
                "height": "170",
                "spellchecker_language": null,
                "spellchecker_is_enabled": false,
                "autogrow": true,
                "richtext": true,
                "placeholder": "",
                "includedInPDF": true,
                "specialChars": null,
                "restrictedEditingMode": false,
                "showToolbar": true,
                "enabledTools": [],
                "disabledTools": false,
            }
        }];
        
        const objProperties = {
            type: "response-textbox-new",
            isResponse: true,
            label: 'CMS.LAYOUT-MANAGER.NEW-OBJECT.RESPONSE-TEXTBOX-CK5.LABEL',
            cssClass: "new-response-textbox-object",
            icon: "cms/icon-response-textbox.png",
            description: 'CMS.LAYOUT-MANAGER.NEW-OBJECT.RESPONSE-TEXTBOX-CK5.DESCRIPTION',
            group: {
                id: 1
            }
        };
        
        ContentObjectRegistryService.register(obj, objProperties);
    }]);

    // Register the directive
    module.directive('asfResponseTextboxNewObject', ['$compile', '$bridges', '$config', 'TaskDataService', '$rootScope', '$interval', '$http', '$signals',
        function($compile, $bridges, $config, TaskDataService, $rootScope, $interval, $http, $signals) {
            return {
                restrict: 'EA',
                replace: true,
                scope: {
                    "contentObject": "="
                },
                templateUrl: "/views/ib/contentobjects/response-textbox-ck5.html",
                controller: ['$injector', '$scope', '$rootScope', '$signals', '$timeout', 'SheetLoadedService', '$q', '$bridges', '$translate', AsfResponseTextboxObjectNewCtrl],
            };
        }
    ]);
}
